import {
  buildMicrositePublicUrls,
  resolveMicrositeHostDomain,
} from "@/lib/micrositeTenantHost";
import { MICROSITE_TENANTS } from "@/lib/seo/micrositeTenants";
import { IS_MULTI_TENANT } from "@/lib/tenancy";
import { resolveTenantKeyFromHost } from "@/lib/tenantKey";
import { getEntryPage } from "@/utils/utils";

const FIRST_REFERRER_KEY = "cp_first_referrer";

function readAdParams(searchParams: URLSearchParams) {
  return {
    ad_device: searchParams.get("ad_device") || searchParams.get("device") || "",
    ad_network: searchParams.get("ad_network") || searchParams.get("network") || "",
    ad_creative: searchParams.get("ad_creative") || searchParams.get("creative") || "",
    ad_placement: searchParams.get("ad_placement") || searchParams.get("placement") || "",
    ad_keyword_matchtype:
      searchParams.get("ad_keyword_matchtype") ||
      searchParams.get("keyword_matchtype") ||
      "",
    ad_clickID:
      searchParams.get("ad_clickID") ||
      searchParams.get("gclid") ||
      searchParams.get("fbclid") ||
      "",
    clickIdentifier:
      searchParams.get("clickIdentifier") ||
      searchParams.get("gclid") ||
      searchParams.get("fbclid") ||
      "",
  };
}

function captureFirstReferrer() {
  if (typeof window === "undefined") return "";
  try {
    const existing = sessionStorage.getItem(FIRST_REFERRER_KEY);
    if (existing) return existing;
    const ref = document.referrer?.trim() || "Direct";
    sessionStorage.setItem(FIRST_REFERRER_KEY, ref);
    return ref;
  } catch {
    return document.referrer?.trim() || "Direct";
  }
}

/** Client-only LSQ pass-through for course microsites (not persisted in campus DB). */
export function buildMicrositeLsqPassThrough(pageCategory?: string): Record<string, unknown> {
  // Microsite LSQ is a multi-tenant-only concern; standalone never builds a pass-through.
  if (!IS_MULTI_TENANT) return {};
  if (typeof window === "undefined") return {};

  const tenantKey = resolveTenantKeyFromHost(window.location.host);
  if (!tenantKey || !MICROSITE_TENANTS.has(tenantKey)) return {};

  const hostDomain = resolveMicrositeHostDomain(tenantKey);
  if (!hostDomain) return {};

  const { landingPage, platformUrl } = buildMicrositePublicUrls(tenantKey, getEntryPage());
  const searchParams = new URLSearchParams(window.location.search);
  const media = searchParams.get("media") || searchParams.get("utm_source") || "";
  const campaign = searchParams.get("campaign") || searchParams.get("utm_campaign") || "";
  const referrer = document.referrer?.trim();

  return {
    hostDomain,
    landingPage,
    platformUrl,
    pageCategory: pageCategory || "home",
    referrerAttribute: media || (referrer ? referrer : "Direct"),
    first_referrer: captureFirstReferrer(),
    utmSource: media || undefined,
    utmCampaign: campaign || undefined,
    ...readAdParams(searchParams),
  };
}

export function isMicrositeTenantKey(tenantKey: string | undefined): boolean {
  return !!tenantKey && MICROSITE_TENANTS.has(tenantKey);
}

/** True on course microsite hosts (tenantByHost.json) — lead drop 4 LSQ only. Multi-tenant only. */
export function isMicrositeLsqLeadDropTenant(): boolean {
  if (!IS_MULTI_TENANT) return false;
  if (typeof window === "undefined") return false;
  return isMicrositeTenantKey(resolveTenantKeyFromHost(window.location.host));
}

/**
 * Lead drop 4 on a course microsite: add the two page facts to the `lsq` pass-through the
 * server builds the LSQ body from — `landingPage` (first page of the session) and
 * `platformUrl` (the page the lead is dropped on).
 *
 * The CMS `lsq_options` never carried them, and the server no longer guesses either one
 * from the other or from the referrer, so without this both would reach LSQ as "". The
 * captured values win over anything in `lsq_options`: a static CMS value cannot know which
 * page this visitor opened. Attached whatever the CMS destination says, because the server
 * sends every microsite drop to LSQ. Not a microsite → the integration is returned untouched.
 */
export function withMicrositeLsqPageUrls<T extends { lsq?: Record<string, unknown> }>(
  integration: T,
): T {
  if (!isMicrositeLsqLeadDropTenant()) return integration;
  const tenantKey = resolveTenantKeyFromHost(window.location.host);
  const { landingPage, platformUrl } = buildMicrositePublicUrls(tenantKey, getEntryPage());
  return { ...integration, lsq: { ...(integration.lsq ?? {}), landingPage, platformUrl } };
}

/** Host domain for LSQ `course` (e.g. langchain.co.in); undefined when not a microsite tenant. */
export function getMicrositeHostDomainForLeadDrop(): string | undefined {
  if (!IS_MULTI_TENANT) return undefined;
  if (typeof window === "undefined") return undefined;
  const tenantKey = resolveTenantKeyFromHost(window.location.host);
  if (!isMicrositeTenantKey(tenantKey)) return undefined;
  return resolveMicrositeHostDomain(tenantKey);
}
